import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { FirebaseAuthContext } from 'libs.firebase_react';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { downloadFromSignedUrl } from '../utils/file_utils';
const NOTIFICATION_MESSAGES = {
    NETWORK_ERROR: 'Network error: please try again in a moment',
    EXPORT_PREPARING: 'Export package is getting prepared',
    EXPORT_PREPARING_SUBTITLE: 'Your download will start automatically',
    EXPORT_SUCCESS: 'Package exported',
    EXPORT_SUCCESS_SUBTITLE: 'Your download should start automatically',
    EXPORT_PARTIAL: 'Some screen reports could not be exported',
    EXPORT_FAILED: 'Export failed',
    UNKNOWN_ERROR: 'An unknown error occurred',
};
// Mirror the job hook's e2e-timeout trick so tests don't flake on lingering toasts.
const NOTIFICATION_TIMEOUT = process.env.STORAGE_IMPLEMENTATION === 'firebase-emulator' ? 5000 : 0;
/**
 * Synchronous single-asset export. Unlike `useLocalizationExportV2` (which enqueues a
 * job and tracks it to completion, requiring the user to click "Download"), this posts
 * to the synchronous `/translation_v2/export/sync` endpoint, which runs the export inline
 * and returns the produced file's signed URL. The file then downloads automatically.
 *
 * Uses the CLOUD endpoint (not the Firebase Hosting rewrite, whose 60s timeout a
 * cold-cache screen-report render can exceed) — same transport as the v1 sync hook.
 */
export const useLocalizationExportSyncV2 = () => {
    const translate = useLocalizeMessage();
    const { post } = useApiClient(ApiClientService.CLOUD);
    const { addNotification } = useToastNotification();
    const { firebaseToken } = useContext(FirebaseAuthContext);
    const handleExport = async ({ resources, scope, viewport, study, assets, productVersion, }) => {
        if (!firebaseToken) {
            throw new Error(translate(NOTIFICATION_MESSAGES.NETWORK_ERROR));
        }
        addNotification({
            title: translate(NOTIFICATION_MESSAGES.EXPORT_PREPARING),
            subtitle: translate(NOTIFICATION_MESSAGES.EXPORT_PREPARING_SUBTITLE),
            type: 'info',
            timeout: NOTIFICATION_TIMEOUT,
        });
        const response = await post('/translation_v2/export/sync', { resources, scope, viewport, study, assets, ...(productVersion ? { productVersion } : {}) }, {
            withCredentials: false,
            headers: {
                Authorization: `Bearer ${firebaseToken}`,
            },
        });
        return response.data;
    };
    return useMutation({
        mutationKey: ['translation', 'export'],
        mutationFn: handleExport,
        onSuccess: async (data) => {
            if (data?.signedUrl) {
                try {
                    await downloadFromSignedUrl(data.signedUrl, data.fileName);
                }
                catch (e) {
                    console.error(e);
                }
            }
            if (data?.screenErrors?.length) {
                // The file still downloads; some screen reports were skipped.
                addNotification({
                    title: translate(NOTIFICATION_MESSAGES.EXPORT_PARTIAL),
                    type: 'warning',
                });
                return;
            }
            addNotification({
                title: translate(NOTIFICATION_MESSAGES.EXPORT_SUCCESS),
                subtitle: translate(NOTIFICATION_MESSAGES.EXPORT_SUCCESS_SUBTITLE),
                type: 'success',
                timeout: NOTIFICATION_TIMEOUT,
            });
        },
        onError: (error) => {
            addNotification({
                title: translate(NOTIFICATION_MESSAGES.EXPORT_FAILED),
                subtitle: error.message || translate(NOTIFICATION_MESSAGES.UNKNOWN_ERROR),
                type: 'error',
            });
        },
    });
};
